import { useLocation } from '@solidjs/router'
import { type Component, createEffect, ErrorBoundary, type JSX, Show } from 'solid-js'
import Button from '~/components/Button'
import Icon from '~/components/Icon'
import { useAuth } from '~/contexts/AuthContext'
import { useNavigate } from '~/router'

/** Rutas públicas exactas. */
const PUBLIC_EXACT = ['/']
/**
 * Públicas por prefijo: '/componentes' cubre también '/componentes/button', y
 * '/foundation' las páginas de colores y tipografía de esa misma biblioteca.
 * '/f' son los formularios publicados, '/p' las páginas del constructor y
 * '/catalogo' la tienda: las tres se abren con el enlace, sin sesión. Si una
 * ruta pública falta aquí, el guard manda al login y el enlace que se comparte
 * con el cliente no lleva a ninguna parte.
 */
const PUBLIC_PREFIXES = ['/componentes', '/foundation', '/f', '/p', '/catalogo']
/**
 * Sólo para invitados: un usuario autenticado no tiene nada que hacer aquí.
 * Ojo: /admin/select-company NO entra — requiere sesión y es donde aterriza
 * un usuario logueado que cambia de empresa.
 */
const GUEST_ONLY = ['/login', '/recuperar']

/** Donde aterriza un usuario ya autenticado: el panel, no la landing. */
const ADMIN_HOME = '/admin'

const App: Component<{ children: JSX.Element }> = props => {
  const auth = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const isPublic = () => PUBLIC_EXACT.includes(location.pathname) || PUBLIC_PREFIXES.some(p => location.pathname === p || location.pathname.startsWith(`${p}/`))

  const isGuestOnly = () => GUEST_ONLY.includes(location.pathname)

  const needsLogin = () => !auth.isAuthenticated() && !isPublic() && !isGuestOnly()
  const alreadyLoggedIn = () => auth.isAuthenticated() && isGuestOnly()

  /**
   * Mientras se valida el token guardado no se decide nada: tener un token no
   * es tener sesión, y navegar antes de saberlo llevaba a pintar el panel con
   * una sesión caducada. Las rutas públicas no esperan.
   */
  const waiting = () => auth.checking() && !isPublic()

  // useLocation es reactivo: a diferencia de window.location.pathname, esto
  // vuelve a evaluarse en cada navegación del SPA, no sólo en el primer render.
  createEffect(() => {
    if (waiting()) return
    if (needsLogin()) {
      navigate('/login', { replace: true })
      return
    }
    if (alreadyLoggedIn()) {
      navigate(ADMIN_HOME, { replace: true })
    }
  })

  /**
   * Red de seguridad por pantalla.
   *
   * Ya hay un `ErrorBoundary` en `index.tsx`, pero envuelve la aplicación
   * entera: cuando algo revienta ahí arriba se cae todo a la vez, y si el fallo
   * ocurre montando la ruta ni siquiera llega a pintarse su aviso — la página se
   * queda **en blanco y sin una sola línea en la consola**. Así fue como una
   * señal leída antes de declararse dejó muerta la pantalla del FileInput sin
   * dejar rastro de por qué.
   *
   * Con el límite aquí, un error de una pantalla se queda en esa pantalla, se
   * ve escrito y queda registrado en la consola para poder depurarlo.
   */
  return (
    <Show when={!waiting() && !needsLogin() && !alreadyLoggedIn()}>
      <ErrorBoundary
        fallback={(err, reset) => {
          // Un fallo que no se registra es un fallo que no se puede arreglar.
          console.error('[pantalla] error no controlado:', err)

          return (
            <div class="flex min-h-dvh flex-col items-center justify-center p-6 text-center">
              <div class="mb-6 rounded-2xl bg-danger-soft p-4">
                <Icon icon="material-symbols-light:error-outline" size={56} class="text-danger" />
              </div>
              <h2 class="mb-2 font-semibold text-foreground text-xl">Esta pantalla no se pudo mostrar</h2>
              <p class="mb-6 max-w-md text-foreground-subtle">{String(err)}</p>
              <div class="flex gap-3">
                <Button onClick={reset} prependIcon="material-symbols-light:refresh">
                  Reintentar
                </Button>
                <Button variant="outlined" color="default" onClick={() => navigate('/')} prependIcon="material-symbols-light:home">
                  Ir al inicio
                </Button>
              </div>
            </div>
          )
        }}
      >
        {props.children}
      </ErrorBoundary>
    </Show>
  )
}

export default App
